// frontend/src/pages/admin/ManageResearchPage.tsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, PlusCircle, MoreHorizontal, Search, Edit3, Trash2, Eye, Loader2, AlertCircle, FileWarning, LayoutDashboard } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

import { type Research as ResearchAdminView, type ResearchStatus as ModelResearchStatusType, type FetchResearchParams } from '@/types'; // Use a more specific type if needed, or extend Research
import { getResearchItems, deleteResearchItem as apiDeleteResearch } from '@/api/researchService'; // Ensure deleteResearch is imported
import { Label } from '@/components/ui/label';

const researchStatuses: ModelResearchStatusType[] = ["draft", "published", "archived"];

const ManageResearchPage = () => {
  const [researchItems, setResearchItems] = useState<ResearchAdminView[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ModelResearchStatusType | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

const fetchAdminResearchItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // --- FIX: Use the strongly-typed FetchResearchParams type ---
      const params: FetchResearchParams = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const fetchedItems = await getResearchItems(params);
      setResearchItems(fetchedItems);
      
      const currentSearchParams = new URLSearchParams();
      if (searchTerm) currentSearchParams.set('search', searchTerm);
      if (statusFilter !== 'all') currentSearchParams.set('status', statusFilter);
      if (searchParams.toString() !== currentSearchParams.toString()) {
          setSearchParams(currentSearchParams, { replace: true });
      }

    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError("Unauthorized. Please log in again.");
        localStorage.removeItem('authToken');
        navigate('/login?sessionExpired=true');
      } else {
        setError("Failed to fetch research items. Please try again later.");
      }
      console.error("Error fetching admin research items:", err);
      setResearchItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, statusFilter, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    setSearchTerm(searchParams.get('search') || '');
    setStatusFilter((searchParams.get('status') as ModelResearchStatusType | 'all') || 'all');
  }, [searchParams]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchAdminResearchItems();
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [fetchAdminResearchItems]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };
  
  const getFullPdfUrl = (relativePath?: string | null) => {
    if (!relativePath) return '#';
    return `${backendBaseUrl}/static/${relativePath.startsWith('/') ? relativePath.substring(1) : relativePath}`;
  };

  const getStatusBadgeVariant = (status: ModelResearchStatusType): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'published': return 'default';
      case 'draft': return 'secondary';
      case 'archived': return 'outline';
      default: return 'secondary';
    }
  };

  const handleDelete = async (researchId: number) => {
    if (!window.confirm("Are you sure you want to delete this research item? This action cannot be undone.")) return;
    setIsDeleting(researchId);
    setError(null);
    try {
      await apiDeleteResearch(researchId);
      fetchAdminResearchItems(); // Refresh list
    } catch (err: any) {
      console.error("Failed to delete research item:", err);
      setError(err.response?.data?.detail || "Failed to delete research item.");
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading && researchItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading research items...</p>
      </div>
    );
  }
  
  if (error && researchItems.length === 0) {
     return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Research</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={fetchAdminResearchItems} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <BookOpen className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage <span className="text-gradient-maroon-to-gold">Research</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              Oversee, upload, and organize all research papers.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mt-4 sm:mt-0">
             <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
            <Button asChild className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
              <Link to="/admin/research/new">
                <PlusCircle className="mr-2 h-5 w-5" /> Upload New Research
              </Link>
            </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
        <div className="relative">
          <Label htmlFor="search-research-admin" className="block text-sm font-medium text-foreground mb-1">Search</Label>
          <Input
            id="search-research-admin"
            type="search"
            placeholder="Search by title, uploader..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 bottom-2.5 h-5 w-5 text-muted-foreground" />
        </div>
        <div>
            <Label htmlFor="status-filter" className="block text-sm font-medium text-foreground mb-1">Status</Label>
            <Select value={statusFilter} onValueChange={(value: ModelResearchStatusType | 'all') => setStatusFilter(value)}>
                <SelectTrigger id="status-filter" className="w-full md:w-[180px] !bg-input border-border focus:border-primary focus:ring-primary cursor-pointer">
                    <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                    <SelectItem value="all" className='cursor-pointer'>All Statuses</SelectItem>
                    {researchStatuses.map(s => <SelectItem key={s} value={s} className="capitalize cursor-pointer">{s}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
      </div>
      
      {error && !isLoading && researchItems.length > 0 && (
         <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
           <AlertCircle className="h-5 w-5 mr-2" /> {error}
         </div>
      )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-vedasMaroon-800">
              <TableRow className="border-border/50">
                <TableHead className="w-[40%] text-primary-foreground py-4">Title</TableHead>
                <TableHead className="hidden md:table-cell text-primary-foreground">Uploader</TableHead>
                <TableHead className="text-primary-foreground">Status</TableHead>
                <TableHead className="hidden sm:table-cell text-primary-foreground">Uploaded</TableHead>
                <TableHead className="text-right text-primary-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && researchItems.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                     <div className="flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                        Loading research items...
                    </div>
                </TableCell></TableRow>
              ) : researchItems.length > 0 ? (
                researchItems.map((item) => (
                  <TableRow key={item.id} className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10">
                    <TableCell className="font-medium text-foreground">{item.title}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{item.uploader_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(item.status as ModelResearchStatusType)} className="capitalize">
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{formatDate(item.upload_date)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger className='justify-self-end' asChild>
                           {isDeleting === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4 cursor-pointer" />}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover text-popover-foreground border-border">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                           <DropdownMenuItem asChild className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <a href={getFullPdfUrl(item.pdf_url)} target="_blank" rel="noopener noreferrer">
                              <Eye className="mr-2 h-4 w-4 text-primary/80" /> View PDF
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <Link to={`/admin/research/edit/${item.id}`}>
                              <Edit3 className="mr-2 h-4 w-4 text-primary/80" /> Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border/50"/>
                          <DropdownMenuItem
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                            disabled={isDeleting === item.id}
                          >
                            {isDeleting === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                             Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} className="text-center py-16 text-muted-foreground">No research items found. { (searchTerm || statusFilter !== 'all') && "Try adjusting your search or filters."}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageResearchPage;